import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation // 🔥 Importante para las animaciones entre páginas
} from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';

// ======== Páginas públicas ========
import Welcome from './pages/Welcome';
import Register from './pages/Register';
import VerifyCode from './pages/VerifyCode';
import Login from './pages/Login';
import UpdatePassword from './pages/UpdatePassword';
import CompleteProfile from './pages/CompleteProfile';
import ForgotPassword from './pages/ForgotPassword';
import VerifyResetCode from './pages/VerifyResetCode';
import ResetPassword from './pages/ResetPassword';
import RatesPage from './pages/RatesPage';


// ======== Páginas privadas ========
import Home from './pages/Home';
import History from './pages/History';
import Profile from './pages/Profile';
import AdminPanel from './pages/admin/AdminPanel';

// ======== Flujo de envíos ========
import Step1 from './pages/flows/SelectCountries';
import Step2 from './pages/flows/PaymentStep';
import Step3 from './pages/flows/PaymentDestinationScreen';
import Step4 from './pages/flows/PaymentSummaryStep';
import Step5 from './pages/flows/PantallaMetodoPago';
import ResumenFinal from './pages/ResumenFinal';

// ======== Otros ========
import PushNotificationSimulator from './components/PushNotificationSimulator';

// =====================================================
// 🔄 Pantalla de Carga Premium (Reutilizable)
// =====================================================
const LoadingScreen = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#f4f7fb]">
    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
    <p className="text-slate-400 text-xs font-bold uppercase tracking-widest animate-pulse">
      Cargando...
    </p>
  </div>
);

// =====================================================
// 🔐 Ruta protegida (usuarios normales)
// =====================================================
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useApp();

  if (loading) return <LoadingScreen />;

  if (!isAuthenticated) return <Navigate to="/" replace />;

  if (user && (!user.first_name || !user.last_name)) {
    return <Navigate to="/complete-profile" replace />;
  }

  return children;
};

// =====================================================
// 🛡 Ruta protegida SOLO para administradores
// =====================================================
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useApp();

  if (loading) return <LoadingScreen />;

  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!user) return null;

  if (user.role?.toLowerCase().trim() !== 'administrador') {
    return <Navigate to="/home" replace />;
  }

  return children;
};

// =====================================================
// 🧭 Definición de rutas (CON ANIMACIONES)
// =====================================================
const AppRoutes = () => {
  const { isAuthenticated } = useApp();
  const location = useLocation(); // 🔥 Captura la ruta actual para Framer Motion

  return (
    /* 🔥 AnimatePresence permite que las páginas hagan transiciones suaves al entrar y salir */
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Públicas */}
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/home" replace /> : <Welcome />}
        />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-code" element={<VerifyCode />} />
        <Route path="/login" element={<Login />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Recuperación de contraseña */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-reset-code" element={<VerifyResetCode />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Perfil incompleto */}
        <Route path="/complete-profile" element={<CompleteProfile />} />

        <Route path="/tasas" element={<RatesPage />} />

        {/* Privadas */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <History />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />

        {/* Flujo de envíos */}
        <Route path="/send/step1" element={<ProtectedRoute><Step1 /></ProtectedRoute>} />
        <Route path="/send/step2" element={<ProtectedRoute><Step2 /></ProtectedRoute>} />
        <Route path="/send/step3" element={<ProtectedRoute><Step3 /></ProtectedRoute>} />
        <Route path="/send/step4" element={<ProtectedRoute><Step4 /></ProtectedRoute>} />
        <Route path="/send/step5" element={<ProtectedRoute><Step5 /></ProtectedRoute>} />

        <Route
          path="/resumen-final"
          element={
            <ProtectedRoute>
              <ResumenFinal />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

// =====================================================
// 🚀 App principal
// =====================================================
const App = () => {
  return (
    <AppProvider>
      <Router>
        <PushNotificationSimulator />
        <AppRoutes />
      </Router>
    </AppProvider>
  );
};

export default App;

